import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Target, Square, CheckCircle, XCircle, Clock, Loader, ChevronDown,
    ChevronRight, AlertTriangle, Send, RotateCcw, Zap, Brain
} from 'lucide-react';
import { taskApi } from '../api/client';

interface TaskStep {
    id: string;
    description: string;
    toolCall?: { tool: string; parameters: Record<string, unknown> };
    dependsOn: string[];
    status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'awaiting_approval';
    retryCount: number;
    error?: string;
}

interface TaskPlan {
    id: string;
    goal: string;
    steps: TaskStep[];
    riskLevel: number;
    status: string;
    summary?: string;
}

interface QueuedTask {
    goal: {
        id: string;
        description: string;
        priority: 'low' | 'medium' | 'high' | 'critical';
        source: string;
        createdAt: string;
    };
    plan?: TaskPlan;
    result?: {
        success: boolean;
        summary: string;
        stepsCompleted: number;
        stepsFailed: number;
        totalDurationMs: number;
    };
    status: 'queued' | 'planning' | 'executing' | 'completed' | 'failed' | 'cancelled';
    queuedAt: string;
    completedAt?: string;
}

interface TaskStats {
    total: number;
    queued: number;
    executing: number;
    completed: number;
    failed: number;
}

export function TaskManager() {
    const queryClient = useQueryClient();
    const [newGoal, setNewGoal] = useState('');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
    const [expandedTask, setExpandedTask] = useState<string | null>(null);

    const { data: taskData, isLoading } = useQuery({
        queryKey: ['tasks'],
        queryFn: () => taskApi.list(),
        refetchInterval: 3000,
    });

    const tasks = (taskData?.tasks ?? []) as QueuedTask[];
    const stats = (taskData?.stats ?? { total: 0, queued: 0, executing: 0, completed: 0, failed: 0 }) as TaskStats;

    const submitGoal = useMutation({
        mutationFn: ({ description, priority: p }: { description: string; priority: string }) =>
            taskApi.submit(description, p),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            setNewGoal('');
        },
    });

    const cancelTask = useMutation({
        mutationFn: (taskId: string) => taskApi.cancel(taskId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
    });

    const approveStep = useMutation({
        mutationFn: ({ stepId, approved }: { stepId: string; approved: boolean }) =>
            taskApi.approveStep(stepId, approved),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
    });

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!newGoal.trim()) return;
        submitGoal.mutate({ description: newGoal.trim(), priority });
    }, [newGoal, priority, submitGoal]);

    const getPriorityColor = (p: string) => {
        switch (p) {
            case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/30';
            case 'high': return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
            case 'medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
            case 'low': return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
            default: return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'queued': return <Clock className="w-4 h-4 text-slate-400" />;
            case 'planning': return <Brain className="w-4 h-4 text-purple-400 animate-pulse" />;
            case 'executing': return <Loader className="w-4 h-4 text-cyan-400 animate-spin" />;
            case 'completed': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
            case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
            case 'cancelled': return <Square className="w-4 h-4 text-slate-500" />;
            default: return <Clock className="w-4 h-4 text-slate-400" />;
        }
    };

    const getStepStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <Clock className="w-3.5 h-3.5 text-slate-500" />;
            case 'running': return <Loader className="w-3.5 h-3.5 text-cyan-400 animate-spin" />;
            case 'completed': return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
            case 'failed': return <XCircle className="w-3.5 h-3.5 text-red-400" />;
            case 'skipped': return <ChevronRight className="w-3.5 h-3.5 text-slate-600" />;
            case 'awaiting_approval': return <AlertTriangle className="w-3.5 h-3.5 text-amber-400 animate-pulse" />;
            default: return <Clock className="w-3.5 h-3.5 text-slate-500" />;
        }
    };

    const formatDuration = (ms: number) => {
        if (ms < 1000) return ms + 'ms';
        if (ms < 60000) return (ms / 1000).toFixed(1) + 's';
        return (ms / 60000).toFixed(1) + 'm';
    };

    return (
        <div className="space-y-4">
            {/* Stats Bar */}
            <div className="grid grid-cols-5 gap-3">
                {[
                    { label: 'Total', value: stats.total, color: 'text-white' },
                    { label: 'Queued', value: stats.queued, color: 'text-yellow-400' },
                    { label: 'Running', value: stats.executing, color: 'text-cyan-400' },
                    { label: 'Completed', value: stats.completed, color: 'text-emerald-400' },
                    { label: 'Failed', value: stats.failed, color: 'text-red-400' },
                ].map(stat => (
                    <div key={stat.label} className="bg-slate-800/50 rounded-lg p-3 text-center border border-slate-700/50">
                        <div className={'text-2xl font-bold ' + stat.color}>{stat.value}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Submit Goal */}
            <form onSubmit={handleSubmit} className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-medium text-white">Submit Autonomous Goal</span>
                </div>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newGoal}
                        onChange={e => setNewGoal(e.target.value)}
                        placeholder="e.g. Investigate suspicious logins for sarah@company.com"
                        className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30"
                    />
                    <select
                        value={priority}
                        onChange={e => setPriority(e.target.value as typeof priority)}
                        className="bg-slate-900/50 border border-slate-600/50 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                    >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                    </select>
                    <button
                        type="submit"
                        disabled={!newGoal.trim() || submitGoal.isPending}
                        className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                    >
                        {submitGoal.isPending ? (
                            <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        Deploy
                    </button>
                </div>
            </form>

            {/* Task List */}
            <div className="space-y-2">
                {isLoading ? (
                    <div className="text-center py-8 text-slate-400">
                        <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading tasks...
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                        <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No tasks yet. Submit a goal to get started.</p>
                    </div>
                ) : (
                    tasks.map(task => {
                        const isExpanded = expandedTask === task.goal.id;
                        const isActive = task.status === 'executing' || task.status === 'planning';
                        const progress = task.plan
                            ? Math.round((task.plan.steps.filter(s => s.status === 'completed' || s.status === 'skipped').length / task.plan.steps.length) * 100)
                            : 0;

                        return (
                            <div
                                key={task.goal.id}
                                className={'bg-slate-800/50 rounded-xl border transition-all ' + (isActive ? 'border-cyan-500/40 shadow-lg shadow-cyan-500/5' : 'border-slate-700/50')}
                            >
                                {/* Task Header */}
                                <div
                                    className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-700/20 rounded-t-xl"
                                    onClick={() => setExpandedTask(isExpanded ? null : task.goal.id)}
                                >
                                    {isExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    )}

                                    {getStatusIcon(task.status)}

                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white truncate">{task.goal.description}</div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className={'text-[10px] px-1.5 py-0.5 rounded border ' + getPriorityColor(task.goal.priority)}>
                                                {task.goal.priority.toUpperCase()}
                                            </span>
                                            <span className="text-[10px] text-slate-500">{task.goal.source}</span>
                                            {task.result?.totalDurationMs && (
                                                <span className="text-[10px] text-slate-500">
                                                    {formatDuration(task.result.totalDurationMs)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Progress indicator for active tasks */}
                                    {isActive && task.plan && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full transition-all duration-500"
                                                    style={{ width: progress + '%' }}
                                                />
                                            </div>
                                            <span className="text-xs text-cyan-400 font-mono">{progress}%</span>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    {isActive && (
                                        <button
                                            onClick={e => { e.stopPropagation(); cancelTask.mutate(task.goal.id); }}
                                            className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors flex-shrink-0"
                                            title="Cancel task"
                                        >
                                            <Square className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>

                                {/* Expanded Step View */}
                                {isExpanded && (
                                    <div className="border-t border-slate-700/50 px-4 py-3">
                                        {task.plan ? (
                                            <div className="space-y-1.5">
                                                <div className="text-xs text-slate-400 mb-2 flex items-center gap-2">
                                                    <span>{task.plan.steps.length} steps</span>
                                                    <span>&bull;</span>
                                                    <span>Risk: {task.plan.riskLevel}/100</span>
                                                </div>
                                                {task.plan.steps.map((step, idx) => {
                                                    let stepClass = 'flex items-start gap-2 py-1.5 px-2 rounded-lg text-sm';
                                                    if (step.status === 'running') stepClass += ' bg-cyan-500/5 border border-cyan-500/20';
                                                    else if (step.status === 'awaiting_approval') stepClass += ' bg-amber-500/5 border border-amber-500/20';

                                                    let textClass = 'text-xs ';
                                                    if (step.status === 'completed') textClass += 'text-slate-300';
                                                    else if (step.status === 'failed') textClass += 'text-red-300';
                                                    else if (step.status === 'running') textClass += 'text-white';
                                                    else if (step.status === 'skipped') textClass += 'text-slate-600';
                                                    else textClass += 'text-slate-400';

                                                    return (
                                                        <div key={step.id} className={stepClass}>
                                                            <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                                                                {getStepStatusIcon(step.status)}
                                                                {idx < task.plan!.steps.length - 1 && (
                                                                    <div className={'w-px h-4 mt-1 ' + (step.status === 'completed' ? 'bg-emerald-500/30' : 'bg-slate-700')} />
                                                                )}
                                                            </div>

                                                            <div className="flex-1 min-w-0">
                                                                <div className={textClass}>{step.description}</div>
                                                                {step.toolCall && (
                                                                    <div className="text-[10px] text-slate-600 mt-0.5 font-mono">
                                                                        {step.toolCall.tool}({Object.keys(step.toolCall.parameters).join(', ')})
                                                                    </div>
                                                                )}
                                                                {step.error && (
                                                                    <div className="text-[10px] text-red-400 mt-0.5">{step.error}</div>
                                                                )}

                                                                {step.status === 'awaiting_approval' && (
                                                                    <div className="flex gap-2 mt-1.5">
                                                                        <button
                                                                            onClick={() => approveStep.mutate({ stepId: step.id, approved: true })}
                                                                            className="text-[10px] px-2 py-1 bg-emerald-600/30 text-emerald-300 rounded hover:bg-emerald-600/50 transition-colors"
                                                                        >
                                                                            Approve
                                                                        </button>
                                                                        <button
                                                                            onClick={() => approveStep.mutate({ stepId: step.id, approved: false })}
                                                                            className="text-[10px] px-2 py-1 bg-red-600/30 text-red-300 rounded hover:bg-red-600/50 transition-colors"
                                                                        >
                                                                            Deny
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {step.retryCount > 0 && (
                                                                <div className="flex items-center gap-0.5 text-[10px] text-amber-400 flex-shrink-0">
                                                                    <RotateCcw className="w-3 h-3" />
                                                                    {step.retryCount}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-slate-500 text-center py-2">
                                                {task.status === 'planning' ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <Brain className="w-4 h-4 animate-pulse text-purple-400" />
                                                        Planning task steps...
                                                    </span>
                                                ) : (
                                                    'Waiting in queue...'
                                                )}
                                            </div>
                                        )}

                                        {task.result && (
                                            <div className={'mt-3 p-2.5 rounded-lg border text-xs ' + (task.result.success ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300' : 'bg-red-500/5 border-red-500/20 text-red-300')}>
                                                <div className="font-medium mb-1">
                                                    {task.result.success ? 'Task Completed' : 'Task Failed'}
                                                </div>
                                                <div className="text-slate-400">
                                                    {task.result.stepsCompleted}/{task.result.stepsCompleted + task.result.stepsFailed} steps completed
                                                    {task.result.totalDurationMs ? ' in ' + formatDuration(task.result.totalDurationMs) : ''}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
